import { Notifications } from "common/Notifications";
import { useGtmAuthorisation } from "core/analytics/google-tag-manager/google-tag-manger.hooks";
import { usePermissionsChecker } from "core/auth/Permissions";
import { Permission } from "core/common-types/permission.enum";
import { useCurrentUserQuery } from "core/gateways/user/UserHooks";
import { BaseIntervalWarning } from "modules/booking/pages/appointmentTypes/warnings/appointments-setting-base-interval-warning";
import { MultiApptWarning } from "modules/booking/pages/appointmentTypes/warnings/appointments-setting-multi-appt-warning";
import { ComponentType, createElement, PropsWithChildren } from "react";
import { useMatch } from "react-router-dom";

import {
  Customizer,
  IMessageBarProps,
  mergeStyles,
  MessageBarType,
  Stack
} from "@bps/fluent-ui";

import { routes } from "../common/routes/routes";
import { useCoreStore } from "../core/stores";
import { InfoBarRestriction } from "./InfoBarRestriction";
import { NavBarRouter } from "./nav-bar/NavBarRouter";

export interface MainLayoutProps {
  toolbar?: ComponentType;
}

const messageBarSettings: Partial<IMessageBarProps> = {
  styles: props => ({
    root: props.messageBarType === MessageBarType.info && {
      background: props.theme.palette.white
    }
  })
};

export const MainLayout = ({
  toolbar,
  children
}: PropsWithChildren<MainLayoutProps>) => {
  const userQuery = useCurrentUserQuery();
  const hasPermissions = usePermissionsChecker();

  const bhbPathMatch = useMatch(routes.appointmentBooking.basePath);
  const settingsPathMmatch = useMatch(routes.settings.bestHealthBooking);

  const isIntendedForThePath = !!bhbPathMatch || !!settingsPathMmatch;

  const { locationId } = useCoreStore();
  useGtmAuthorisation(userQuery.data, locationId);

  const mainStyles = mergeStyles({
    flexGrow: 1,
    backgroundColor: "#f3f3f4",
    overflow: "auto",
    padding: "8px 16px 8px 16px",
    position: "relative"
  });

  const toolbarStyles = mergeStyles({
    padding: "0 16px",
    margin: "4px 0"
  });

  return (
    <Stack verticalFill>
      <NavBarRouter />
      {isIntendedForThePath && hasPermissions(Permission.BHB_READ) && (
        <>
          {locationId && <BaseIntervalWarning />}
          {locationId && <MultiApptWarning />}
          <InfoBarRestriction />
        </>
      )}

      {toolbar && (
        <div id="toolbar" className={toolbarStyles}>
          {createElement(toolbar)}
        </div>
      )}
      <Customizer
        scopedSettings={{
          MessageBar: messageBarSettings
        }}
      >
        <Notifications />
      </Customizer>
      <main className={mainStyles}>{children}</main>
    </Stack>
  );
};
